import AccordionItem from '@github-ui/brand-core/components/Accordion/AccordionItem'
import {PlusIcon} from '@primer/octicons-react'
import {Button, Link, Text} from '@primer/react'
import {clsx} from 'clsx'
import {useEffect, useMemo, useRef} from 'react'

import type {ProductAnnouncementModalTabContentProps} from './../../ProductAnnouncement/ProductAnnouncement.types'

import Styles from './ProductAnnouncementModalTabContent.module.css'

export type {ProductAnnouncementModalTabContentProps} from './../../ProductAnnouncement/ProductAnnouncement.types'

const defaultProps: Partial<ProductAnnouncementModalTabContentProps> = {}

const ProductAnnouncementModalTabContent = (props: ProductAnnouncementModalTabContentProps) => {
  const initializedProps: ProductAnnouncementModalTabContentProps = {...defaultProps, ...props}
  const {title, description, cta1, cta2, isExpanded, onAccordionToggle, shouldShowTabCtas, onEvent} = initializedProps

  const descriptionRef = useRef<HTMLDivElement>(null)
  const isAccordionItem = !!onAccordionToggle

  const headingDOM = useMemo(() => {
    return (
      <div className={Styles['ProductAnnouncementModalTabContent__heading']}>
        <Text as="h3" size="large" className={Styles['ProductAnnouncementModalTabContent__heading-title']}>
          {title}
        </Text>

        {isAccordionItem && <PlusIcon size={24} />}
      </div>
    )
  }, [isAccordionItem, title])

  const contentDOM = useMemo(() => {
    return (
      <div className={Styles['ProductAnnouncementModalTabContent__content']}>
        <Text
          ref={descriptionRef}
          as="p"
          size="medium"
          // eslint-disable-next-line react/forbid-component-props
          dangerouslySetInnerHTML={{__html: description}}
        />

        <div className={Styles['ProductAnnouncementModalTabContent__content-ctas']}>
          {shouldShowTabCtas && cta1 && (
            <Button
              as="a"
              href={cta1.href}
              variant="primary"
              target={cta1.isNewWindow ? '_blank' : undefined}
              onClick={() => onEvent?.('tab_cta_1_clicked', {url: cta1.href})}
            >
              {cta1.label}
            </Button>
          )}
          {shouldShowTabCtas && cta2 && (
            <Button
              as="a"
              href={cta2.href}
              target={cta2.isNewWindow ? '_blank' : undefined}
              onClick={() => onEvent?.('tab_cta_2_clicked', {url: cta2.href})}
            >
              {cta2.label}
            </Button>
          )}

          {!shouldShowTabCtas && cta2 && (
            <Link
              href={cta2.href}
              target={cta2.isNewWindow ? '_blank' : undefined}
              onClick={() => onEvent?.('tab_link_clicked', {url: cta2.href})}
            >
              {cta2.label}
            </Link>
          )}
        </div>
      </div>
    )
  }, [description, shouldShowTabCtas, cta1, cta2, onEvent])

  useEffect(() => {
    if (!descriptionRef.current) return

    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (target.tagName === 'A') {
        const url = (target as HTMLAnchorElement).href
        const text = target.textContent || ''
        onEvent?.('tab_description_link_clicked', {url, text})
      }
    }

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        onClick(event as unknown as MouseEvent)
      }
    }

    const links = Array.from(descriptionRef.current.getElementsByTagName('a'))
    for (const link of links) {
      link.addEventListener('click', onClick)
      link.addEventListener('keyup', onKeyUp)
    }

    return () => {
      for (const link of links) {
        link.removeEventListener('click', onClick)
        link.removeEventListener('keyup', onKeyUp)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {isAccordionItem ? (
        <AccordionItem
          className={clsx(
            Styles['ProductAnnouncementModalTabContent'],
            isAccordionItem && Styles['ProductAnnouncementModalTabContent--accordion'],
            isExpanded && Styles['ProductAnnouncementModalTabContent--expanded'],
          )}
          onToggle={onAccordionToggle}
          isExpanded={!!isExpanded}
          heading={headingDOM}
          content={contentDOM}
          isListItem
        />
      ) : (
        <div className={Styles['ProductAnnouncementModalTabContent']}>
          {headingDOM}
          {contentDOM}
        </div>
      )}
    </>
  )
}

export default ProductAnnouncementModalTabContent
